import
{
    forkJoin as observableForkJoin,
    of as observableOf,
    BehaviorSubject,
    Observable
} from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { InitializationModel } from "./initialization.model";
import { LoggerUtil } from "../LoggerService";
import { InitializationStatusCodes } from './index';
import { addProvider, IProviderDescriptor } from "../ServiceUtil";
import { ConfigService } from '../ConfigService';
import { AuthenticationService } from '../AuthenticationService';
import { DashboardService } from '../Dashboard';
import { ClientService } from '../ClientService';
import { AuthStatusService } from '../AuthStatusService';
import { ISession } from '../AuthenticationService/authentication.interface';
import { EmployeeService } from '../EmployeeService';
import { VendorService } from '../VendorService';
import { AccountManagerService } from '../AccountManagerService/account.manager.service';
import { HttpProvider } from '../http/http.provider';

export class InitializationService
{
    static logger: LoggerUtil = LoggerUtil.getLoggerUtil("InitializationService");

    private _state: string = undefined;

    private _defaultState: string = InitializationStatusCodes.UNCONFIGURED;

    private initStateSubject: BehaviorSubject<string>;

    protected bootstrapExecuted: boolean = false; // Flag to prevent duplicate bootstrap calls


    protected set __state(value: string)
    {
        if (this._state === value) return;


        this._state = value;

        this.initializationModel.setInitializationState(value);

        // Reset bootstrap flag when going to unauthenticated state (for logout scenarios)
        if (value === InitializationStatusCodes.UNAUTHENTICATED) {
            InitializationService.logger.debug('Resetting bootstrap flag due to UNAUTHENTICATED state');
            this.bootstrapExecuted = false;
        }

        if (value === InitializationStatusCodes.RUNNING ||
            value === InitializationStatusCodes.UNAUTHENTICATED ||
            value === InitializationStatusCodes.RESTPASSWORD ||
            value === InitializationStatusCodes.RESTREQUEST)
        {
            this.initStateSubject.next(value);
        }

    }

    public get state(): string
    {
        return this._state;
    }

    public initState: Observable<string>;

    constructor(protected configService: ConfigService,
        private authenticationService: AuthenticationService,
        private initializationModel: InitializationModel,
        private authStatusService: AuthStatusService,
        private accountService: AccountManagerService,
        private httpProvider: HttpProvider)
    {
        this.__state = this._defaultState;
        this.initStateSubject = new BehaviorSubject(this.state);
        this.initState = this.initStateSubject;

        this.initStateSubject.next(this.state);
        const url = window.location.href;
        if (url.indexOf('reset-password') > -1 ||
            url.indexOf('reset-req') > -1)
        {
            this.__state = url.indexOf('reset-password') > -1 ? InitializationStatusCodes.RESTPASSWORD : InitializationStatusCodes.RESTREQUEST;
            return;
        }

        // Check initial session state immediately
        this.checkInitialAuthenticationState();

        this.authenticationService.userSession.subscribe((session: ISession) =>
        {
            InitializationService.logger.debug('User session changed:', session);
            InitializationService.logger.debug('Current initialization state:', this.state);

            // If session is restored and we're in RESUMING state, proceed with initialization
            if (session && (session.loggedin || session.authenticated) && this.state === InitializationStatusCodes.RESUMING)
            {
                InitializationService.logger.debug('Session restored during RESUMING - checking if bootstrap already executed');
                if (!this.bootstrapExecuted) {
                    InitializationService.logger.debug('Bootstrap not executed yet - proceeding with bootstrap');
                    this.executeBootstrapCalls();
                } else {
                    InitializationService.logger.debug('Bootstrap already executed - skipping duplicate call');
                }
                return;
            }

            if (session && session.loggedin === true
                && (this.state === InitializationStatusCodes.UNCONFIGURED
                    || this.state === InitializationStatusCodes.UNINITIALIZED
                    || this.state === InitializationStatusCodes.UNAUTHENTICATED))
            {
                InitializationService.logger.debug('Session is logged in - starting initialization');
                this.init();
            }
            else if (session && session.authenticated === true
                && (this.state === InitializationStatusCodes.UNCONFIGURED
                    || this.state === InitializationStatusCodes.UNINITIALIZED
                    || this.state === InitializationStatusCodes.UNAUTHENTICATED))
            {
                InitializationService.logger.debug('Session is authenticated - starting initialization');
                this.init();
            }
            else if (!session || (!session.loggedin && !session.authenticated)) 
            {
                InitializationService.logger.debug('Session is invalid - checking if logout was triggered');
                
                // If we were previously running and now session is invalid, this is likely a logout
                if (this.state === InitializationStatusCodes.RUNNING) {
                    InitializationService.logger.debug('User logged out - setting UNAUTHENTICATED without re-initialization');
                    this.__state = InitializationStatusCodes.UNAUTHENTICATED;
                    return;
                }
                
                // Only set UNAUTHENTICATED if we're not currently in RESUMING state
                // This prevents interrupting server validation during app startup
                if (this.state !== InitializationStatusCodes.RESUMING && 
                    this.state !== InitializationStatusCodes.INITIALIZING &&
                    this.state !== InitializationStatusCodes.RUNNING) {
                    InitializationService.logger.debug('Setting UNAUTHENTICATED state');
                    this.__state = InitializationStatusCodes.UNAUTHENTICATED;
                } else {
                    InitializationService.logger.debug('Not setting UNAUTHENTICATED - currently in protected state:', this.state);
                }
            }
        });

        // Subscribe to session extension events from HttpProvider
        this.httpProvider.onSessionExtended.subscribe(() =>
        {
            InitializationService.logger.debug('Session automatically extended by server');
        });
    }

    /**
     * Check initial authentication state when app starts.
     * This ensures we check with server first before redirecting to login.
     * On app refresh, JWT cookies might still be valid even if session is cleared.
     */
    private checkInitialAuthenticationState(): void
    {
        InitializationService.logger.debug('Checking initial authentication state...');
        
        const currentSession = this.authenticationService.session;
        InitializationService.logger.debug('Initial session data:', currentSession);

        // Always try server validation first on app startup, regardless of existing session
        // This handles both refresh scenarios and validates existing sessions
        InitializationService.logger.debug('Attempting server validation to confirm authentication status');
        
        // Set RESUMING state to indicate we're validating with server
        this.__state = InitializationStatusCodes.RESUMING;
        
        this.authStatusService.getProfile().subscribe(
            (result: boolean) =>
            {
                InitializationService.logger.debug('Server validation result:', result);
                
                if (result)
                {
                    InitializationService.logger.debug('Server validation successful - user authenticated via JWT cookies');
                    
                    // The AuthStatusService.getProfile() automatically calls updateSession()
                    // which should trigger the userSession observable and update the session
                    // Let's wait a moment and then check if session was updated
                    setTimeout(() => {
                        const restoredSession = this.authenticationService.session;
                        InitializationService.logger.debug('Session after server validation:', restoredSession);
                        
                        // Proceed with bootstrap regardless of session state since server confirmed auth
                        if (!this.bootstrapExecuted) {
                            InitializationService.logger.debug('Bootstrap not executed yet - proceeding with bootstrap after successful server validation');
                            this.executeBootstrapCalls();
                        } else {
                            InitializationService.logger.debug('Bootstrap already executed - skipping duplicate call after server validation');
                        }
                    }, 50); // Small delay to allow session update
                } else
                {
                    InitializationService.logger.debug('Server validation failed - no valid authentication');
                    this.__state = InitializationStatusCodes.UNAUTHENTICATED;
                }
            },
            (error) =>
            {
                InitializationService.logger.debug('Server validation failed with error - redirecting to login:', error);
                this.__state = InitializationStatusCodes.UNAUTHENTICATED;
            }
        );
    }

    public init(): void
    {
        InitializationService.logger.debug(`init( State = ${this.state} )`);

        this.__state = InitializationStatusCodes.INITIALIZING;

        // Get the current session directly and proceed with bootstrap
        const currentSession = this.authenticationService.session;
        InitializationService.logger.debug('Current session data:', currentSession);

        // Validate session before proceeding
        if (!this.isValidSession(currentSession)) {
            InitializationService.logger.warn('Session validation failed during init - setting UNAUTHENTICATED');
            this.clearSessionAndSetUnauthenticated();
            return;
        }

        // Proceed with bootstrap
        this.performBootstrap(currentSession);
    }

    /**
     * Validate if a session object is valid and contains required authentication data.
     */
    private isValidSession(session: ISession): boolean
    {
        if (!session) {
            InitializationService.logger.debug('Session validation: session is null/undefined');
            return false;
        }

        if (!session.loggedin && !session.authenticated) {
            InitializationService.logger.debug('Session validation: session not logged in or authenticated');
            return false;
        }

        // Additional validation - check for essential session properties
        if (session.loggedin && !session.userId) {
            InitializationService.logger.debug('Session validation: logged in but no userId');
            return false;
        }

        InitializationService.logger.debug('Session validation: session is valid');
        return true;
    }

    private performBootstrap(session: ISession): void
    {
        InitializationService.logger.debug('Starting bootstrap with session:', session);

        if (session && (session.loggedin || session.authenticated))
        {
            InitializationService.logger.debug('User has valid session - proceeding with bootstrap');
            InitializationService.logger.debug('Session details - loggedin:', session.loggedin, 'authenticated:', session.authenticated);

            this.__state = InitializationStatusCodes.INITIALIZING;

            // Try AuthStatusService validation first (optional)
            if (!this.authenticationService.hasAuthCallMade())
            {
                InitializationService.logger.debug('Attempting to use AuthStatusService for session validation');
                this.__state = InitializationStatusCodes.RESUMING;

                this.authStatusService.getProfile().subscribe(
                    (result: boolean) =>
                    {
                        if (result)
                        {
                            InitializationService.logger.debug('AuthStatusService validation successful - proceeding with bootstrap');
                            this.executeBootstrapCalls();
                        } else
                        {
                            InitializationService.logger.error('AuthStatusService validation failed - session invalid');
                            this.clearSessionAndSetUnauthenticated();
                        }
                    },
                    (error) =>
                    {
                        InitializationService.logger.debug('AuthStatusService endpoints not available - using existing session for bootstrap');
                        // If AuthStatusService fails but we have a session, try bootstrap anyway
                        if (session && (session.loggedin || session.authenticated)) {
                            this.executeBootstrapCalls();
                        } else {
                            InitializationService.logger.error('No valid session and AuthStatusService failed');
                            this.clearSessionAndSetUnauthenticated();
                        }
                    }
                );
            }
            else
            {
                InitializationService.logger.debug('Auth call already made, proceeding directly to bootstrap');
                this.executeBootstrapCalls();
            }
        }
        else
        {
            InitializationService.logger.debug('No valid session found - setting UNAUTHENTICATED state');
            this.clearSessionAndSetUnauthenticated();
        }
    }

    /**
     * Clear any invalid session data and set UNAUTHENTICATED state.
     * This ensures proper login redirect.
     */
    private clearSessionAndSetUnauthenticated(): void
    {
        InitializationService.logger.debug('Setting UNAUTHENTICATED state to trigger login redirect');
        
        // Set unauthenticated state to trigger login redirect
        this.__state = InitializationStatusCodes.UNAUTHENTICATED;
    }

    protected executeBootstrapCalls(): void
    {
        // Check if bootstrap has already been executed to prevent duplicates
        if (this.bootstrapExecuted) {
            InitializationService.logger.debug('Bootstrap already executed - skipping duplicate call');
            return;
        }

        InitializationService.logger.debug('Executing bootstrap API calls...');
        
        // Set flag to prevent duplicate calls
        this.bootstrapExecuted = true;
        
        // Double-check session state before proceeding
        const currentSession = this.authenticationService.session;
        InitializationService.logger.debug('Session state before bootstrap:', currentSession);

        // Set state to INITIALIZING
        this.__state = InitializationStatusCodes.INITIALIZING;

        // Create observables with individual error handling
        const configByOrgObs = this.configService.getConfigByOrg().pipe(
            map(result =>
            {
                InitializationService.logger.debug('getConfigByOrg result:', result);
                return { api: 'getConfigByOrg', success: true, data: result };
            }),
            catchError(error =>
            {
                InitializationService.logger.error('getConfigByOrg failed:', error);
                return observableOf({ api: 'getConfigByOrg', success: false, error: error });
            })
        );

        const accountManagerObs = this.accountService.getAllAccountManager().pipe(
            map(result =>
            {
                InitializationService.logger.debug('getAllAccountManager result:', result);
                return { api: 'getAllAccountManager', success: true, data: result };
            }),
            catchError(error =>
            {
                InitializationService.logger.error('getAllAccountManager failed:', error);
                return observableOf({ api: 'getAllAccountManager', success: false, error: error });
            })
        );

        const observables: Array<Observable<any>> = [
            configByOrgObs,
            accountManagerObs
        ];

        InitializationService.logger.debug('Starting bootstrap API calls...');

        observableForkJoin(observables).subscribe(
            (apiCallStatus: Array<any>) =>
            {
                InitializationService.logger.debug('Bootstrap API calls completed. Results:', apiCallStatus);

                // Log any failures but allow app to start
                const failedCalls = apiCallStatus.filter(result => result.success === false);
                if (failedCalls.length > 0)
                {
                    InitializationService.logger.warn('Some bootstrap calls failed but app will continue:', failedCalls);
                }

                // Check session one more time before setting RUNNING
                const finalSession = this.authenticationService.session;
                InitializationService.logger.debug('Final session state before RUNNING:', finalSession);

                InitializationService.logger.debug('App initialization successful - app is RUNNING');
                this.__state = InitializationStatusCodes.RUNNING;
            },
            (error) =>
            {
                InitializationService.logger.error(`Bootstrap failed: ${JSON.stringify(error)}`);
                
                // Reset bootstrap flag on failure so it can be retried
                this.bootstrapExecuted = false;
                
                // If bootstrap fails, check if it's an auth issue
                if (error && (error.status === 401 || error.status === 403)) {
                    InitializationService.logger.error('Bootstrap failed due to authentication - redirecting to login');
                    this.__state = InitializationStatusCodes.UNAUTHENTICATED;
                } else {
                    InitializationService.logger.error('Bootstrap failed due to other errors - redirecting to login');
                    this.__state = InitializationStatusCodes.UNAUTHENTICATED;
                }
            }
        );
    }
}